import React, { useEffect, useState } from "react";
import { X, AlertTriangle, Trash2 } from "lucide-react";
import api, { formatError } from "@/lib/api";

/**
 * Two-step destructive confirmation: shows the impact (counts of children
 * that will be cascade-deleted) and requires the admin to type the room name
 * verbatim before the delete button activates.
 */
export default function ConfirmDeleteRoom({ room, onClose, onDeleted }) {
  const [stats, setStats] = useState(null);
  const [confirm, setConfirm] = useState("");
  const [understand, setUnderstand] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/rooms/${room.id}/stats`).then((r) => setStats(r.data)).catch(() => {});
  }, [room.id]);

  const matches = confirm === room.name;

  const submit = async (e) => {
    e.preventDefault();
    if (!matches || !understand) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/rooms/${room.id}`, { params: { confirm: room.name } });
      onDeleted?.(room);
    } catch (e) { setErr(formatError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-card border border-destructive/40 rounded-md w-full max-w-lg p-6"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-destructive/15 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-destructive" />
            </div>
            <div>
              <h3 className="font-display text-xl tracking-tight">Delete data room</h3>
              <div className="label-mono text-muted-foreground mt-1">This action cannot be undone.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close">
            <X className="w-5 h-5 text-muted-foreground hover:text-foreground" />
          </button>
        </div>

        <div className="mt-5 border border-border rounded-md p-4 space-y-2 text-sm">
          <div>
            You are about to permanently delete{" "}
            <span className="font-display text-base">{room.name}</span> along with everything below.
          </div>
          {stats ? (
            <div className="grid grid-cols-3 gap-2 mt-3">
              <Stat label="Documents" v={stats.documents} />
              <Stat label="Folders" v={stats.folders} />
              <Stat label="Members" v={stats.members} />
              <Stat label="Questions" v={stats.questions} />
              <Stat label="Checklist" v={stats.checklist_items} />
              <Stat label="Activity logs" v={stats.activity_events} />
            </div>
          ) : (
            <div className="label-mono text-muted-foreground mt-2">— Calculating impact —</div>
          )}
        </div>

        <div className="mt-5 space-y-3">
          <label className="block">
            <div className="label-mono">
              Type <span className="text-destructive">{room.name}</span> to confirm
            </div>
            <input
              data-testid="confirm-delete-input"
              autoFocus
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className={`mt-1 w-full px-3 py-2.5 bg-background border rounded-md focus:outline-none focus:ring-2 ${
                matches ? "border-destructive ring-destructive/40" : "border-border"
              }`}
            />
          </label>
          <label className="flex items-start gap-2 text-sm text-muted-foreground cursor-pointer">
            <input
              data-testid="confirm-delete-checkbox"
              type="checkbox" checked={understand}
              onChange={(e) => setUnderstand(e.target.checked)}
              className="mt-0.5 accent-destructive"
            />
            <span>
              I understand that all documents, members, questions, and analytics for this
              data room will be permanently erased.
            </span>
          </label>
        </div>

        {err && <div className="mt-3 text-sm text-destructive">{err}</div>}

        <div className="mt-6 flex gap-3 justify-end">
          <button
            type="button" onClick={onClose}
            className="px-4 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
          >
            Cancel
          </button>
          <button
            data-testid="confirm-delete-submit"
            type="submit"
            disabled={!matches || !understand || busy}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-destructive text-destructive-foreground hover:opacity-90 transition disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
            {busy ? "Deleting…" : "Permanently delete"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-secondary px-3 py-2 rounded-md">
      <div className="mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="font-display text-lg mt-0.5">{v}</div>
    </div>
  );
}
